import React, { useEffect, useRef } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import DynamicTheme from './Theme';
import { useTheme } from '../../context/ThemeContext';
import { getSeriesNames, normalizeSeriesData } from './chartDataUtils';

const StackedColumnChart = ({ data, title, scale, titleFontSize, legendFontSize }) => {
    const chartRef = useRef(null);
    const { themeName } = useTheme();

    const scaleFactor = scale / 100;
    const resolvedTitleFontSize = titleFontSize ?? "1.5em";
    const resolvedLegendFontSize = legendFontSize ?? "0.8em";
    const resolvedAxisFontSize = (0.8 * scaleFactor) + "em";

    useEffect(() => {
        if (data && chartRef.current) {
            const chartData = normalizeSeriesData(data);
            const root = am5.Root.new(chartRef.current);
            root.setThemes([am5themes_Animated.new(root), DynamicTheme.new(root)]);
            root._renderer.resolution = 2;

            root.container.set("background", am5.Rectangle.new(root, {
                fill: am5.color(0xffffff),
                fillOpacity: 0
            }));

            const seriesNames = getSeriesNames(chartData);

            const chart = root.container.children.push(
                am5xy.XYChart.new(root, {
                    panX: true,
                    panY: true,
                    wheelX: 'panX',
                    wheelY: 'zoomX',
                    layout: root.verticalLayout,
                })
            );

            if (title) {
                chart.children.unshift(am5.Label.new(root, {
                    text: title,
                    fontSize: resolvedTitleFontSize,
                    fontWeight: "500",
                    textAlign: "center",
                    x: am5.p50,
                    centerX: am5.p50
                }));
            }

            const legend = chart.children.push(
                am5.Legend.new(root, {
                    centerX: am5.p50,
                    x: am5.p50
                })
            );
            legend.labels.template.setAll({
                fontSize: resolvedLegendFontSize,
                fontWeight: "300"
            });
            legend.valueLabels.template.setAll({
                fontSize: resolvedLegendFontSize,
                fontWeight: "300"
            });

            const xAxis = chart.xAxes.push(
                am5xy.CategoryAxis.new(root, {
                    categoryField: 'xaxis',
                    renderer: am5xy.AxisRendererX.new(root, { minGridDistance: 30 }),
                    tooltip: am5.Tooltip.new(root, {})
                })
            );
            xAxis.get("renderer").labels.template.setAll({
                oversizedBehavior: "wrap",
                textAlign: "center",
                maxWidth: 100,
                fontSize: resolvedAxisFontSize,
            });

            const yAxis = chart.yAxes.push(
                am5xy.ValueAxis.new(root, {
                    renderer: am5xy.AxisRendererY.new(root, {})
                })
            );
            yAxis.get("renderer").labels.template.setAll({
                oversizedBehavior: "wrap",
                textAlign: "center",
                maxWidth: 100,
                fontSize: resolvedAxisFontSize,
            });

            function makeSeries(fieldName) {
                const series = chart.series.push(
                    am5xy.ColumnSeries.new(root, {
                        name: fieldName,
                        stacked: true,
                        xAxis: xAxis,
                        yAxis: yAxis,
                        valueYField: fieldName,
                        categoryXField: 'xaxis'
                    })
                );

                series.columns.template.setAll({
                    tooltipText: "[fontSize: 1em]{name} - {categoryX}: {valueY}",
                    width: am5.percent(90),
                    tooltipY: 0,
                    strokeOpacity: 0
                });

                series.data.setAll(chartData);
                series.appear();
                legend.data.push(series);
            }

            seriesNames.forEach((seriesName) => {
                makeSeries(seriesName);
            });

            xAxis.data.setAll(chartData);

            chart.appear(1000, 100);

            return () => {
                root.dispose();
            };
        }
    }, [data, scale, title, themeName, titleFontSize, legendFontSize]);

    return <div ref={chartRef} style={{ width: '100%', height: '100%' }}></div>;
};

export default StackedColumnChart;
